// Expense catalog — the tenant's master list of pass-through items that can
// be billed on to a customer (software seats, subscriptions, filing fees…).
// Each entry carries a recurrence period describing how often its cost is
// incurred. Canonical home for that vocabulary so the API
// (schema/validators/import), the typed clients, and the web UI all derive
// from one source and can never drift.
//
// Stored values are stable lowercase codes; the human-facing label lives
// alongside in EXPENSE_PERIOD_LABELS.
//
// This vocabulary deliberately overlaps with `BUDGET_RESET_PERIODS` in
// `budgets.ts` (monthly / quarterly / annual) but is NOT imported from it and
// must not become so. A budget reset period governs when an hours allowance
// rolls over; an expense period describes a recurring charge's cadence. They
// share three codes today by coincidence of English, not by shared meaning —
// budgets additionally carry `semi_annual`, which no expense uses, and either
// list must be free to grow without dragging the other along.

export const EXPENSE_PERIODS = ['monthly', 'quarterly', 'annual'] as const;
export type ExpensePeriod = (typeof EXPENSE_PERIODS)[number];

export const EXPENSE_PERIOD_LABELS: Record<ExpensePeriod, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
};

/** Length of one expense period in calendar months. */
export const EXPENSE_PERIOD_MONTHS: Record<ExpensePeriod, number> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
};

export function isExpensePeriod(v: unknown): v is ExpensePeriod {
  return typeof v === 'string' && (EXPENSE_PERIODS as readonly string[]).includes(v);
}

/**
 * Human label for a stored period code.
 *
 * `$type<ExpensePeriod>()` on the column is a compile-time cast, not a runtime
 * guarantee — a row written by a migration, an import, or a relaxed CHECK can
 * carry anything. Rather than render `undefined` into the UI (which reads as
 * a rendering bug, not a data problem), fall back to the raw stored value so
 * the operator can see exactly what is in the column (G15).
 */
export function expensePeriodLabel(period: string): string {
  return isExpensePeriod(period) ? EXPENSE_PERIOD_LABELS[period] : period;
}
